/**
 * Credit Analyzer Page - Auxilio Finance
 * AI-powered credit report interpretation and improvement strategies
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheckIcon,
  PlusIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  LightBulbIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon,
  ClockIcon,
  TrashIcon,
  XMarkIcon,
  CreditCardIcon,
  BanknotesIcon,
  HomeIcon,
  AcademicCapIcon,
  TruckIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import apiClient from '../services/api';
import { Button } from '../components/ui/Button';
import LoadingOverlay from '../components/ui/LoadingOverlay';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { useThemeStore } from '../store/themeStore';

interface CreditAccount {
  name: string;
  account_type: string;
  balance: number;
  credit_limit?: number;
  status: string;
  payment_history?: string;
  age_months?: number;
  is_authorized_user: boolean;
}

interface CreditInquiry {
  creditor: string;
  date: string;
  inquiry_type: string;
}

interface NegativeItem {
  item_type: string;
  creditor: string;
  amount?: number;
  date_reported?: string;
  status: string;
}

interface AnalysisResult {
  id: string;
  analysis_date: string;
  estimated_score?: number;
  score_range?: {
    id: string;
    min: number;
    max: number;
    description: string;
  };
  analysis: {
    utilization: {
      overall_utilization: number;
      total_balance: number;
      total_limit: number;
      per_card: Array<{
        name: string;
        balance: number;
        limit: number;
        utilization: number;
        status: string;
      }>;
      status: string;
    };
    payment_history: {
      late_payment_count: number;
      collection_count: number;
      bankruptcy_count: number;
      issues: Array<{ account: string; status: string; impact: string }>;
      status: string;
    };
    credit_age: {
      average_age_months: number;
      average_age_years: number;
      oldest_account_months: number;
      account_count: number;
      status: string;
    };
    credit_mix: {
      types_present: string[];
      type_count: number;
      has_revolving: boolean;
      has_installment: boolean;
      status: string;
    };
    inquiries: {
      hard_inquiry_count: number;
      soft_inquiry_count: number;
      recent_inquiries: Array<{ creditor: string; date: string }>;
      status: string;
    };
  };
  improvement_plan: Array<{
    title: string;
    priority: string;
    impact: string;
    timeline: string;
    strategy: string;
    current?: string;
    target?: string;
    action?: string;
  }>;
  insights: string[];
  quick_wins: string[];
  long_term_strategies: string[];
  estimated_improvement: {
    potential_points: number;
    timeline_months: number;
  };
  account_count: number;
  total_debt: number;
}

interface ScoreFactor {
  weight: number;
  description: string;
}

const SCORE_COLORS = {
  excellent: '#10b981',
  very_good: '#22c55e',
  good: '#84cc16',
  fair: '#f59e0b',
  poor: '#ef4444',
};

const STATUS_COLORS: Record<string, string> = {
  excellent: '#10b981',
  good: '#22c55e',
  fair: '#f59e0b',
  poor: '#ef4444',
  building: '#3b82f6',
  limited: '#6b7280',
  unknown: '#6b7280',
};

const ACCOUNT_TYPE_ICONS: Record<string, any> = {
  credit_card: CreditCardIcon,
  auto_loan: TruckIcon,
  mortgage: HomeIcon,
  personal_loan: BanknotesIcon,
  student_loan: AcademicCapIcon,
  retail_card: CreditCardIcon,
  home_equity: HomeIcon,
  medical: DocumentTextIcon,
  other: DocumentTextIcon,
};

export const CreditAnalyzer: React.FC = () => {
  const { t } = useTranslation();
  const theme = useThemeStore((state) => state.theme);

  // Determine if we're in dark mode for date input styling
  const isDarkMode = theme === 'dark' ||
    (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const dateInputStyle: React.CSSProperties = { colorScheme: isDarkMode ? 'dark' : 'light' };

  // State
  const [activeTab, setActiveTab] = useState<'input' | 'analysis' | 'tips'>('input');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [scoreFactors, setScoreFactors] = useState<Record<string, ScoreFactor>>({});

  // Input state
  const [estimatedScore, setEstimatedScore] = useState<number | undefined>(undefined);
  const [accounts, setAccounts] = useState<CreditAccount[]>([]);
  const [inquiries, setInquiries] = useState<CreditInquiry[]>([]);
  const [negativeItems, setNegativeItems] = useState<NegativeItem[]>([]);
  const [totalDebt, setTotalDebt] = useState<number>(0);

  // Modal state
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [editingAccountIndex, setEditingAccountIndex] = useState<number | null>(null);
  const [accountForm, setAccountForm] = useState<CreditAccount>({
    name: '',
    account_type: 'credit_card',
    balance: 0,
    credit_limit: undefined,
    status: 'current',
    age_months: undefined,
    is_authorized_user: false,
  });

  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [inquiryForm, setInquiryForm] = useState<CreditInquiry>({
    creditor: '',
    date: new Date().toISOString().split('T')[0],
    inquiry_type: 'hard',
  });

  // Upload state
  const [loadingOverlay, setLoadingOverlay] = useState<{ isVisible: boolean; message: string; subMessage?: string }>({
    isVisible: false,
    message: '',
    subMessage: undefined
  });
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadReferenceData();
    // Don't auto-load saved data - user should upload or enter data manually
    // loadSavedData();
  }, []);

  const loadReferenceData = async () => {
    try {
      const response = await apiClient.get('/credit-analyzer/reference/score-factors');
      setScoreFactors(response.data.factors);
    } catch (err) {
      console.error('Failed to load reference data:', err);
    }
  };

  const loadSavedData = async () => {
    try {
      const response = await apiClient.get('/credit-analyzer/my-credit');
      if (response.data.found) {
        const report = response.data.report;
        if (report) {
          setEstimatedScore(report.estimated_score);
          setAccounts(report.accounts || []);
          setInquiries(report.inquiries || []);
          setNegativeItems(report.negative_items || []);
          setTotalDebt(report.total_debt || 0);
        }
        if (response.data.analysis) {
          setAnalysis(response.data.analysis);
        }
      }
    } catch (err) {
      console.error('Failed to load saved data:', err);
    }
  };

  const handleAddAccount = () => {
    if (!accountForm.name) {
      setError(t('creditAnalyzer:errors.invalidAccount', 'Please enter an account name'));
      return;
    }

    if (editingAccountIndex !== null) {
      const updated = [...accounts];
      updated[editingAccountIndex] = accountForm;
      setAccounts(updated);
    } else {
      setAccounts([...accounts, accountForm]);
    }

    setShowAccountModal(false);
    setEditingAccountIndex(null);
    resetAccountForm();
    setAnalysis(null);
  };

  const handleEditAccount = (index: number) => {
    setAccountForm(accounts[index]);
    setEditingAccountIndex(index);
    setShowAccountModal(true);
  };

  const handleDeleteAccount = (index: number) => {
    if (window.confirm(t('creditAnalyzer:confirmDeleteAccount', 'Are you sure you want to delete this account?'))) {
      setAccounts(accounts.filter((_, i) => i !== index));
      setAnalysis(null);
    }
  };

  const resetAccountForm = () => {
    setAccountForm({
      name: '',
      account_type: 'credit_card',
      balance: 0,
      credit_limit: undefined,
      status: 'current',
      age_months: undefined,
      is_authorized_user: false,
    });
  };

  const handleAddInquiry = () => {
    if (!inquiryForm.creditor) {
      setError(t('creditAnalyzer:errors.invalidInquiry', 'Please enter a creditor name'));
      return;
    }

    setInquiries([...inquiries, inquiryForm]);
    setShowInquiryModal(false);
    setInquiryForm({
      creditor: '',
      date: new Date().toISOString().split('T')[0],
      inquiry_type: 'hard',
    });
    setAnalysis(null);
  };

  const handleDeleteInquiry = (index: number) => {
    setInquiries(inquiries.filter((_, i) => i !== index));
    setAnalysis(null);
  };

  const handleAnalyze = async () => {
    if (accounts.length === 0 && !estimatedScore) {
      setError(t('creditAnalyzer:errors.noData', 'Please add accounts or enter your estimated credit score'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/credit-analyzer/analyze', {
        estimated_score: estimatedScore,
        accounts: accounts,
        inquiries: inquiries,
        negative_items: negativeItems,
        total_debt: totalDebt,
        language: 'en',
      });
      setAnalysis(response.data);
      setActiveTab('analysis');
    } catch (err: any) {
      setError(err.response?.data?.detail || t('creditAnalyzer:errors.analyzeFailed', 'Failed to analyze credit'));
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = async () => {
    if (window.confirm(t('creditAnalyzer:confirmClearAll', 'Are you sure you want to clear all credit data?'))) {
      setEstimatedScore(undefined);
      setAccounts([]);
      setInquiries([]);
      setNegativeItems([]);
      setTotalDebt(0);
      setAnalysis(null);
      try {
        await apiClient.delete('/credit-analyzer/my-credit');
      } catch (err) {
        console.error('Failed to clear data on server:', err);
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isPdf = file.name.toLowerCase().endsWith('.pdf');
    setLoadingOverlay({
      isVisible: true,
      message: t('creditAnalyzer:importing', 'Importing Credit Report...'),
      subMessage: isPdf
        ? t('creditAnalyzer:importingPdfHint', 'Processing PDF (this may take a moment for scanned documents)')
        : t('creditAnalyzer:importingCsvHint', 'Analyzing and extracting credit information')
    });
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('language', 'en');

      const response = await apiClient.post('/credit-analyzer/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Populate the form with extracted data
      if (response.data) {
        if (response.data.estimated_score) {
          setEstimatedScore(response.data.estimated_score);
        }
        if (response.data.accounts && response.data.accounts.length > 0) {
          setAccounts(response.data.accounts);
        }
        if (response.data.inquiries && response.data.inquiries.length > 0) {
          setInquiries(response.data.inquiries);
        }
        if (response.data.negative_items && response.data.negative_items.length > 0) {
          setNegativeItems(response.data.negative_items);
        }
        if (response.data.total_debt) {
          setTotalDebt(response.data.total_debt);
        }
        // The upload endpoint also returns analysis results
        if (response.data.analysis) {
          setAnalysis(response.data);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || t('creditAnalyzer:errors.uploadFailed', 'Failed to upload and parse file'));
    } finally {
      setLoadingOverlay({ isVisible: false, message: '', subMessage: undefined });
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExportCSV = () => {
    if (accounts.length === 0) return;

    const headers = ['Account Name', 'Account Type', 'Balance', 'Credit Limit', 'Status', 'Age (Months)', 'Is Authorized User'];
    const rows = accounts.map(a => [
      a.name,
      a.account_type,
      a.balance.toString(),
      a.credit_limit?.toString() || '',
      a.status,
      a.age_months?.toString() || '',
      a.is_authorized_user ? 'Yes' : 'No',
    ]);

    let csvContent = [
      `Estimated Credit Score: ${estimatedScore || 'N/A'}`,
      '',
      'ACCOUNTS',
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    if (inquiries.length > 0) {
      csvContent += '\n\nINQUIRIES\nCreditor,Date,Type\n';
      csvContent += inquiries.map(i => `"${i.creditor}","${i.date}","${i.inquiry_type}"`).join('\n');
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `credit_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const generateExportContent = () => {
    let content = `Credit Analysis Summary\n`;
    content += `Generated: ${new Date().toLocaleString()}\n\n`;

    content += `=== CREDIT SCORE ===\n`;
    content += `Estimated Score: ${estimatedScore || 'N/A'}\n\n`;

    content += `=== ACCOUNTS (${accounts.length}) ===\n`;
    accounts.forEach(a => {
      content += `\n${a.name}\n`;
      content += `  Type: ${a.account_type}\n`;
      content += `  Balance: ${formatCurrency(a.balance)}\n`;
      if (a.credit_limit) content += `  Credit Limit: ${formatCurrency(a.credit_limit)}\n`;
      content += `  Status: ${a.status}\n`;
      if (a.age_months) content += `  Age: ${a.age_months} months\n`;
    });

    if (inquiries.length > 0) {
      content += `\n=== INQUIRIES (${inquiries.length}) ===\n`;
      inquiries.forEach(i => {
        content += `${i.creditor} - ${i.date} (${i.inquiry_type})\n`;
      });
    }

    if (analysis) {
      content += `\n=== ANALYSIS ===\n`;
      if (analysis.insights?.length) {
        content += `\nInsights:\n`;
        analysis.insights.forEach(i => content += `- ${i}\n`);
      }
      if (analysis.quick_wins?.length) {
        content += `\nQuick Wins:\n`;
        analysis.quick_wins.forEach(w => content += `- ${w}\n`);
      }
      if (analysis.long_term_strategies?.length) {
        content += `\nLong-term Strategies:\n`;
        analysis.long_term_strategies.forEach(s => content += `- ${s}\n`);
      }
    }

    return content;
  };

  const handleExport = async (format: 'word' | 'pdf' | 'text' | 'csv') => {
    if (format === 'csv') {
      handleExportCSV();
      return;
    }

    try {
      const content = generateExportContent();
      const response = await apiClient.post('/export/document', {
        content,
        title: 'Credit Analysis Summary',
        format,
      }, {
        responseType: 'blob',
      });

      const extension = format === 'word' ? 'docx' : format;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `auxilio_credit_analysis_${new Date().toISOString().slice(0, 10)}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError('Failed to export document');
    }
  };

  const handlePrint = () => {
    const content = generateExportContent();
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Credit Analysis Summary</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; white-space: pre-wrap; }
              h1 { color: #10b981; }
            </style>
          </head>
          <body>
            <h1>Credit Analysis Summary</h1>
            <pre>${content}</pre>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const hasData = accounts.length > 0 || estimatedScore !== undefined;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getScoreColor = (score: number) => {
    if (score >= 800) return SCORE_COLORS.excellent;
    if (score >= 740) return SCORE_COLORS.very_good;
    if (score >= 670) return SCORE_COLORS.good;
    if (score >= 580) return SCORE_COLORS.fair;
    return SCORE_COLORS.poor;
  };

  const getStatusBadgeColor = (status: string) => {
    const color = STATUS_COLORS[status] || '#6b7280';
    return {
      backgroundColor: `${color}20`,
      color: color,
    };
  };

  const getPriorityColor = (priority: string) => {
    if (priority === 'high') return 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400';
    if (priority === 'medium') return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400';
    return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400';
  };

  // Chart data for score factors
  const scoreFactorData = Object.entries(scoreFactors).map(([key, factor]) => ({
    name: key
      .replace('_', ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase()),
    weight: factor.weight,
    fill:
      key === 'payment_history'
        ? '#10b981'
        : key === 'credit_utilization'
        ? '#3b82f6'
        : key === 'credit_age'
        ? '#f59e0b'
        : key === 'credit_mix'
        ? '#8b5cf6'
        : '#ec4899',
  }));

  // Utilization chart data
  const utilizationChartData = analysis?.analysis.utilization.per_card.slice(0, 5).map((card) => ({
    name: card.name.substring(0, 12),
    utilization: card.utilization,
    fill: card.utilization < 10 ? '#10b981' : card.utilization < 30 ? '#22c55e' : card.utilization < 50 ? '#f59e0b' : '#ef4444',
  }));

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <LoadingOverlay
        isVisible={loadingOverlay.isVisible}
        message={loadingOverlay.message}
        subMessage={loadingOverlay.subMessage}
      />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldCheckIcon className="h-8 w-8 text-emerald-500" />
            {t('creditAnalyzer:title', 'Credit Analyzer')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('creditAnalyzer:subtitle', 'Understand your credit report and get personalized improvement strategies')}
          </p>
        </div>

        <div className="flex items-center gap-3 mt-4 md:mt-0">
          {/* Import/Export/Clear */}
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf,.csv,text/csv,.txt"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const ext = file.name.toLowerCase().split('.').pop();
                  if (!['csv', 'pdf', 'txt'].includes(ext || '')) {
                    alert('Please select a CSV, PDF, or TXT file.');
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    return;
                  }
                }
                handleFileUpload(e);
              }}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-emerald-500 dark:hover:text-emerald-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('creditAnalyzer:uploadReport', 'Import Credit Report (CSV/PDF/TXT)')}
            >
              <ArrowUpTrayIcon className="h-5 w-5" />
            </button>
            <ExportDropdownMenu
              onExport={handleExport}
              onPrint={handlePrint}
              onExportCSV={handleExportCSV}
              showCSV={true}
              showPrint={true}
            />
            <button
              onClick={handleClearAll}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={t('creditAnalyzer:clearAll', 'Clear All Data')}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>

          {hasData && (
            <Button onClick={handleAnalyze} disabled={loading}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  {t('creditAnalyzer:analyzing', 'Analyzing...')}
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <SparklesIcon className="h-5 w-5" />
                  {t('creditAnalyzer:analyze', 'Analyze Credit')}
                </span>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
        <button
          onClick={() => setActiveTab('input')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'input'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          {t('creditAnalyzer:tabs.input', 'Credit Profile')}
        </button>
        <button
          onClick={() => setActiveTab('analysis')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'analysis'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
          disabled={!analysis}
        >
          <span className="flex items-center gap-1">
            <SparklesIcon className="h-4 w-4" />
            {t('creditAnalyzer:tabs.analysis', 'Analysis')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('tips')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'tips'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <span className="flex items-center gap-1">
            <LightBulbIcon className="h-4 w-4" />
            {t('creditAnalyzer:tabs.tips', 'Credit Tips')}
          </span>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            {t('common:buttons.ok', 'OK')}
          </button>
        </div>
      )}

      {/* Input Tab */}
      {activeTab === 'input' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Estimated Score */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('creditAnalyzer:estimatedScore', 'Estimated Credit Score')}
              </h3>
              <div className="flex items-center gap-4">
                <input
                  type="number"
                  value={estimatedScore || ''}
                  onChange={(e) => setEstimatedScore(e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="e.g., 720"
                  min="300"
                  max="850"
                  className="w-32 px-4 py-3 text-xl font-bold text-center border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  <p>{t('creditAnalyzer:scoreRange', 'Range: 300-850')}</p>
                  {estimatedScore && (
                    <p
                      className="font-medium mt-1"
                      style={{ color: getScoreColor(estimatedScore) }}
                    >
                      {estimatedScore >= 800
                        ? 'Excellent'
                        : estimatedScore >= 740
                        ? 'Very Good'
                        : estimatedScore >= 670
                        ? 'Good'
                        : estimatedScore >= 580
                        ? 'Fair'
                        : 'Poor'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Credit Accounts */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('creditAnalyzer:accounts', 'Credit Accounts')} ({accounts.length})
                </h3>
                <button
                  onClick={() => {
                    resetAccountForm();
                    setShowAccountModal(true);
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors"
                >
                  <PlusIcon className="h-4 w-4" />
                  {t('creditAnalyzer:addAccount', 'Add Account')}
                </button>
              </div>

              {accounts.length > 0 ? (
                <div className="space-y-3">
                  {accounts.map((account, index) => {
                    const Icon = ACCOUNT_TYPE_ICONS[account.account_type] || DocumentTextIcon;
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{account.name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {formatCurrency(account.balance)}
                              {account.credit_limit && ` / ${formatCurrency(account.credit_limit)}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className="px-2 py-0.5 text-xs rounded-full"
                            style={getStatusBadgeColor(account.status === 'current' ? 'excellent' : 'poor')}
                          >
                            {account.status.replace('_', ' ')}
                          </span>
                          <button
                            onClick={() => handleEditAccount(index)}
                            className="p-1 text-gray-400 hover:text-emerald-500 transition-colors"
                          >
                            <ArrowPathIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteAccount(index)}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                  {t('creditAnalyzer:noAccounts', 'No accounts added yet')}
                </p>
              )}
            </div>

            {/* Inquiries */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('creditAnalyzer:inquiries', 'Hard Inquiries')} ({inquiries.filter((i) => i.inquiry_type === 'hard').length})
                </h3>
                <button
                  onClick={() => setShowInquiryModal(true)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors"
                >
                  <PlusIcon className="h-4 w-4" />
                  {t('creditAnalyzer:addInquiry', 'Add Inquiry')}
                </button>
              </div>

              {inquiries.length > 0 ? (
                <div className="space-y-2">
                  {inquiries.map((inquiry, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm"
                    >
                      <div>
                        <span className="font-medium text-gray-900 dark:text-white">{inquiry.creditor}</span>
                        <span className="text-gray-500 dark:text-gray-400 ml-2">
                          {new Date(inquiry.date).toLocaleDateString()}
                        </span>
                      </div>
                      <button
                        onClick={() => handleDeleteInquiry(index)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                  {t('creditAnalyzer:noInquiries', 'No inquiries recorded')}
                </p>
              )}
            </div>
          </div>

          {/* Right Column - Score Factors */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('creditAnalyzer:scoreFactors', 'Credit Score Factors')}
              </h3>
              {scoreFactorData.length > 0 && (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={scoreFactorData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 40]} tickFormatter={(v) => `${v}%`} />
                    <YAxis type="category" dataKey="name" width={120} />
                    <Tooltip formatter={(value) => `${value}%`} />
                    <Bar dataKey="weight" radius={[0, 4, 4, 0]}>
                      {scoreFactorData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
              <div className="mt-4 space-y-2">
                {Object.entries(scoreFactors).map(([key, factor]) => (
                  <div key={key} className="text-sm">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {key.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())} ({factor.weight}%)
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 ml-2">- {factor.description}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Start */}
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-6 border border-emerald-200 dark:border-emerald-800">
              <h3 className="font-semibold text-emerald-800 dark:text-emerald-200 mb-3">
                {t('creditAnalyzer:quickStart', 'Quick Start')}
              </h3>
              <ol className="space-y-2 text-sm text-emerald-700 dark:text-emerald-300">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs flex-shrink-0">1</span>
                  {t('creditAnalyzer:step1', 'Enter your estimated credit score (optional)')}
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs flex-shrink-0">2</span>
                  {t('creditAnalyzer:step2', 'Add your credit accounts with balances and limits')}
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs flex-shrink-0">3</span>
                  {t('creditAnalyzer:step3', 'Add any recent hard inquiries')}
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs flex-shrink-0">4</span>
                  {t('creditAnalyzer:step4', 'Click "Analyze Credit" for personalized insights')}
                </li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Tab */}
      {activeTab === 'analysis' && analysis && (
        <div className="space-y-6">
          {/* Score Summary */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-6">
              {analysis.estimated_score && (
                <div
                  className="w-28 h-28 rounded-full flex flex-col items-center justify-center text-white"
                  style={{ backgroundColor: getScoreColor(analysis.estimated_score) }}
                >
                  <span className="text-3xl font-bold">{analysis.estimated_score}</span>
                  <span className="text-xs">{analysis.score_range?.id.replace('_', ' ')}</span>
                </div>
              )}
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t('creditAnalyzer:creditSummary', 'Credit Summary')}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('creditAnalyzer:utilization', 'Utilization')}</p>
                    <p
                      className="text-xl font-bold"
                      style={{ color: STATUS_COLORS[analysis.analysis.utilization.status] }}
                    >
                      {analysis.analysis.utilization.overall_utilization.toFixed(1)}%
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('creditAnalyzer:accounts', 'Accounts')}</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{analysis.account_count}</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('creditAnalyzer:avgAge', 'Avg Age')}</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">
                      {analysis.analysis.credit_age.average_age_years?.toFixed(1) || 0} yrs
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('creditAnalyzer:hardInquiries', 'Hard Inquiries')}</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{analysis.analysis.inquiries.hard_inquiry_count}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Estimated Improvement */}
          {analysis.estimated_improvement && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-6 border border-emerald-200 dark:border-emerald-800">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-200 flex items-center gap-2">
                    <ArrowTrendingUpIcon className="h-5 w-5" />
                    {t('creditAnalyzer:potentialImprovement', 'Potential Improvement')}
                  </h3>
                  <p className="text-emerald-700 dark:text-emerald-300 mt-1">
                    {t('creditAnalyzer:followingPlan', 'By following the improvement plan below')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                    +{analysis.estimated_improvement.potential_points} pts
                  </p>
                  <p className="text-sm text-emerald-600 dark:text-emerald-400">
                    in ~{analysis.estimated_improvement.timeline_months} months
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Utilization Chart */}
          {utilizationChartData && utilizationChartData.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('creditAnalyzer:utilizationByCard', 'Utilization by Card')}
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={utilizationChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
                  <Bar dataKey="utilization" radius={[4, 4, 0, 0]}>
                    {utilizationChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex gap-4 mt-2 text-xs">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-green-500" />
                  {'<10% Excellent'}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-lime-500" />
                  {'10-30% Good'}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-amber-500" />
                  {'30-50% Fair'}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-red-500" />
                  {'>50% Poor'}
                </span>
              </div>
            </div>
          )}

          {/* Improvement Plan */}
          {analysis.improvement_plan && analysis.improvement_plan.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('creditAnalyzer:improvementPlan', 'Improvement Plan')}
              </h3>
              <div className="space-y-4">
                {analysis.improvement_plan.map((step, index) => (
                  <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">{step.title}</h4>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${getPriorityColor(step.priority)}`}>
                        {step.priority} priority
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{step.strategy}</p>
                    {step.current && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        <strong>Current:</strong> {step.current}
                      </p>
                    )}
                    {step.action && (
                      <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-1">
                        <strong>Action:</strong> {step.action}
                      </p>
                    )}
                    <div className="flex gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                      <span>Impact: {step.impact}</span>
                      <span>Timeline: {step.timeline}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Wins */}
          {analysis.quick_wins && analysis.quick_wins.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
              <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-3 flex items-center gap-2">
                <CheckCircleIcon className="h-5 w-5" />
                {t('creditAnalyzer:quickWins', 'Quick Wins (30-60 days)')}
              </h3>
              <ul className="space-y-2">
                {analysis.quick_wins.map((win, index) => (
                  <li key={index} className="flex items-start gap-2 text-blue-700 dark:text-blue-300">
                    <CheckCircleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    {win}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* AI Insights */}
          {analysis.insights && analysis.insights.length > 0 && (
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6 border border-purple-200 dark:border-purple-800">
              <h3 className="text-lg font-semibold text-purple-800 dark:text-purple-200 mb-3 flex items-center gap-2">
                <SparklesIcon className="h-5 w-5" />
                {t('creditAnalyzer:aiInsights', 'AI Insights')}
              </h3>
              <ul className="space-y-2">
                {analysis.insights.map((insight, index) => (
                  <li key={index} className="flex items-start gap-2 text-purple-700 dark:text-purple-300">
                    <LightBulbIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    {insight}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Tips Tab */}
      {activeTab === 'tips' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <TipCard
            title={t('creditAnalyzer:tips.utilization.title', 'Credit Utilization')}
            tips={[
              'Keep overall utilization below 30%, ideally below 10%',
              'Pay down balances before statement closing date',
              'Request credit limit increases (without hard pull)',
              'Consider spreading balances across multiple cards',
              'Pay twice per month to keep reported balance low',
            ]}
            color="blue"
          />
          <TipCard
            title={t('creditAnalyzer:tips.paymentHistory.title', 'Payment History')}
            tips={[
              'Set up autopay for at least minimum payments',
              'Use calendar reminders for due dates',
              'Ask creditors for goodwill adjustments for one-time late payments',
              'A single 30-day late can drop your score 60-100 points',
              'Payments 1-29 days late usually don\'t hurt your score',
            ]}
            color="green"
          />
          <TipCard
            title={t('creditAnalyzer:tips.inquiries.title', 'Hard Inquiries')}
            tips={[
              'Multiple inquiries for same loan type within 14-45 days count as one',
              'Hard inquiries impact score for 12 months, stay on report for 2 years',
              'Soft inquiries (checking your own credit) don\'t affect your score',
              'Pre-approval offers are usually soft inquiries',
              'Space out credit applications by at least 6 months',
            ]}
            color="purple"
          />
          <TipCard
            title={t('creditAnalyzer:tips.building.title', 'Building Credit')}
            tips={[
              'Start with a secured credit card if you have no credit',
              'Become an authorized user on a family member\'s old account',
              'Consider credit-builder loans from credit unions',
              'Keep accounts open even if you don\'t use them much',
              'Avoid closing your oldest credit card',
            ]}
            color="amber"
          />
        </div>
      )}

      {/* Add Account Modal */}
      {showAccountModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingAccountIndex !== null
                  ? t('creditAnalyzer:editAccount', 'Edit Account')
                  : t('creditAnalyzer:addAccount', 'Add Account')}
              </h2>
              <button
                onClick={() => {
                  setShowAccountModal(false);
                  setEditingAccountIndex(null);
                  resetAccountForm();
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('creditAnalyzer:accountName', 'Account Name')} *
                </label>
                <input
                  type="text"
                  value={accountForm.name}
                  onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })}
                  placeholder="e.g., Chase Sapphire"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('creditAnalyzer:accountType', 'Account Type')}
                </label>
                <select
                  value={accountForm.account_type}
                  onChange={(e) => setAccountForm({ ...accountForm, account_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="credit_card">Credit Card</option>
                  <option value="auto_loan">Auto Loan</option>
                  <option value="mortgage">Mortgage</option>
                  <option value="personal_loan">Personal Loan</option>
                  <option value="student_loan">Student Loan</option>
                  <option value="retail_card">Retail Card</option>
                  <option value="home_equity">Home Equity</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('creditAnalyzer:balance', 'Balance')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                    <input
                      type="number"
                      value={accountForm.balance || ''}
                      onChange={(e) => setAccountForm({ ...accountForm, balance: parseFloat(e.target.value) || 0 })}
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      min="0"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('creditAnalyzer:creditLimit', 'Credit Limit')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
                    <input
                      type="number"
                      value={accountForm.credit_limit || ''}
                      onChange={(e) =>
                        setAccountForm({
                          ...accountForm,
                          credit_limit: e.target.value ? parseFloat(e.target.value) : undefined,
                        })
                      }
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('creditAnalyzer:status', 'Status')}
                </label>
                <select
                  value={accountForm.status}
                  onChange={(e) => setAccountForm({ ...accountForm, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="current">Current</option>
                  <option value="late_30">30 Days Late</option>
                  <option value="late_60">60 Days Late</option>
                  <option value="late_90">90 Days Late</option>
                  <option value="collection">In Collection</option>
                  <option value="closed">Closed</option>
                  <option value="paid">Paid Off</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('creditAnalyzer:accountAge', 'Account Age (months)')}
                </label>
                <input
                  type="number"
                  value={accountForm.age_months || ''}
                  onChange={(e) =>
                    setAccountForm({
                      ...accountForm,
                      age_months: e.target.value ? parseInt(e.target.value) : undefined,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  min="0"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="authorizedUser"
                  checked={accountForm.is_authorized_user}
                  onChange={(e) => setAccountForm({ ...accountForm, is_authorized_user: e.target.checked })}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                <label htmlFor="authorizedUser" className="text-sm text-gray-700 dark:text-gray-300">
                  {t('creditAnalyzer:authorizedUser', 'I am an authorized user (not primary account holder)')}
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAccountModal(false);
                  setEditingAccountIndex(null);
                  resetAccountForm();
                }}
                fullWidth
              >
                {t('common:buttons.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleAddAccount} fullWidth disabled={!accountForm.name}>
                {editingAccountIndex !== null
                  ? t('common:buttons.save', 'Save')
                  : t('creditAnalyzer:addAccount', 'Add Account')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Inquiry Modal */}
      {showInquiryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('creditAnalyzer:addInquiry', 'Add Inquiry')}
              </h2>
              <button
                onClick={() => setShowInquiryModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('creditAnalyzer:creditor', 'Creditor Name')} *
                </label>
                <input
                  type="text"
                  value={inquiryForm.creditor}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, creditor: e.target.value })}
                  placeholder="e.g., Chase Bank"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('creditAnalyzer:inquiryDate', 'Date')}
                </label>
                <input
                  type="date"
                  value={inquiryForm.date}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  style={dateInputStyle}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('creditAnalyzer:inquiryType', 'Type')}
                </label>
                <select
                  value={inquiryForm.inquiry_type}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, inquiry_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="hard">Hard Inquiry</option>
                  <option value="soft">Soft Inquiry</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="secondary" onClick={() => setShowInquiryModal(false)} fullWidth>
                {t('common:buttons.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleAddInquiry} fullWidth disabled={!inquiryForm.creditor}>
                {t('creditAnalyzer:addInquiry', 'Add Inquiry')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t(
            'common:disclaimer.financial',
            'This information is for educational purposes only and is not financial advice. Please consult a qualified financial professional for personalized guidance.'
          )}
        </p>
      </div>
    </div>
  );
};

// Tip Card Component
const TipCard: React.FC<{ title: string; tips: string[]; color: string }> = ({ title, tips, color }) => {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200',
    green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200',
    purple: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800 text-purple-800 dark:text-purple-200',
    amber: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200',
  };

  const textClasses: Record<string, string> = {
    blue: 'text-blue-700 dark:text-blue-300',
    green: 'text-green-700 dark:text-green-300',
    purple: 'text-purple-700 dark:text-purple-300',
    amber: 'text-amber-700 dark:text-amber-300',
  };

  return (
    <div className={`rounded-xl p-6 border ${colorClasses[color]}`}>
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      <ul className="space-y-2">
        {tips.map((tip, index) => (
          <li key={index} className={`text-sm ${textClasses[color]}`}>
            • {tip}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default CreditAnalyzer;
